import React, { useEffect, useMemo, useRef, useState } from "react";
import mapboxgl from "mapbox-gl";
import {
  buildMdmLocationHistoryEntries,
  formatTime,
  getLocationLogPayload,
} from "../../../functions/mdmDeviceDisplayUtils";
import {
  applyFloorImageLayer,
  branchHasMultipleFloors,
  DEFAULT_FLOOR_ALL,
  getBranchFloorOptions,
  getStoreMapCenter,
  MAPBOX_DEFAULT_BEARING,
  MAPBOX_STYLE,
  parseMapCoordinates,
  setMapboxAccessToken,
} from "../../../functions/branchMapboxUtils";
import MdmLocationVersionMapPanel from "./MdmLocationVersionMapPanel";

const formatCoordinate = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
};

const MdmDeviceLocationTab = ({
  locationSource,
  onLocationSourceChange,
  infoJson,
  parsedLocation,
  locationLogResult,
  locationHistory = [],
  deviceInfo = null,
  retailerId,
  branchId,
  storeCoordinates,
  branchMapData,
  isBranchMapDataLoading,
  branchMapDataError,
  isLocationTabActive,
}) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [floor, setFloor] = useState(DEFAULT_FLOOR_ALL);
  const [mapReady, setMapReady] = useState(false);
  const [focusedHistoryId, setFocusedHistoryId] = useState(null);
  const [isHistoryListExpanded, setIsHistoryListExpanded] = useState(true);

  const historyEntries = useMemo(
    () => buildMdmLocationHistoryEntries(locationHistory, deviceInfo),
    [deviceInfo, locationHistory]
  );

  const historyMapEntries = useMemo(
    () =>
      historyEntries.map((entry) => ({
        deviceId: entry.historyId,
        latitude: entry.latitude,
        longitude: entry.longitude,
        updatedAt: entry.updatedAt,
        appVersion: null,
        hasData: entry.hasData,
      })),
    [historyEntries]
  );

  const focusedHistoryEntry = useMemo(
    () => historyEntries.find((entry) => entry.historyId === focusedHistoryId) || null,
    [focusedHistoryId, historyEntries]
  );

  const activeMdmLocation = useMemo(() => {
    if (focusedHistoryEntry) {
      return {
        locationLatitude: focusedHistoryEntry.latitude,
        locationLongitude: focusedHistoryEntry.longitude,
      };
    }
    if (historyEntries.length) {
      return {
        locationLatitude: historyEntries[0].latitude,
        locationLongitude: historyEntries[0].longitude,
      };
    }
    return parsedLocation;
  }, [focusedHistoryEntry, historyEntries, parsedLocation]);

  const hasNoData =
    locationSource === "mdm"
      ? infoJson === "No device_info found" || !historyEntries.length
      : !parsedLocation;

  const locationLogPayload =
    locationSource === "logs" ? getLocationLogPayload(locationLogResult) : null;
  const appVersion =
    locationLogPayload?.app_version ?? locationLogPayload?.appVersion ?? null;

  const deviceCoordinates = useMemo(
    () => parseMapCoordinates(locationSource === "mdm" ? activeMdmLocation : parsedLocation),
    [activeMdmLocation, locationSource, parsedLocation]
  );
  const storeCenter = useMemo(
    () => getStoreMapCenter(storeCoordinates),
    [storeCoordinates]
  );
  const floorOptions = getBranchFloorOptions(branchMapData);
  const showFloorPicker = branchHasMultipleFloors(branchMapData);

  const hasRetailerBranchIds =
    retailerId !== null &&
    retailerId !== undefined &&
    branchId !== null &&
    branchId !== undefined;
  const canRenderMap = Boolean(hasRetailerBranchIds && storeCoordinates && isLocationTabActive);

  useEffect(() => {
    setFocusedHistoryId(null);
  }, [locationHistory, locationSource]);

  useEffect(() => {
    if (!focusedHistoryId) return;
    const stillExists = historyEntries.some((entry) => entry.historyId === focusedHistoryId);
    if (!stillExists) setFocusedHistoryId(null);
  }, [focusedHistoryId, historyEntries]);

  const locationDetails = hasNoData ? (
    <span className="text-xs text-on-surface-variant">
      {locationSource === "mdm"
        ? "No location history found. Use Refresh to fetch device info."
        : "No location log found. Use Refresh to fetch the location log."}
    </span>
  ) : locationSource === "mdm" ? (
    <span className="text-xs text-on-surface whitespace-nowrap">
      <span className="font-semibold">History:</span> {historyEntries.length} record
      {historyEntries.length === 1 ? "" : "s"}
      {focusedHistoryEntry && (
        <>
          <span className="mx-2 text-on-surface-variant">·</span>
          <span className="font-semibold">Focused:</span>{" "}
          {focusedHistoryEntry.updatedAt
            ? formatTime(focusedHistoryEntry.updatedAt)
            : "selected record"}
        </>
      )}
      {!focusedHistoryEntry && activeMdmLocation && (
        <>
          <span className="mx-2 text-on-surface-variant">·</span>
          <span className="font-semibold">Latitude:</span>{" "}
          {formatCoordinate(activeMdmLocation.locationLatitude)}
          <span className="mx-2 text-on-surface-variant">,</span>
          <span className="font-semibold">Longitude:</span>{" "}
          {formatCoordinate(activeMdmLocation.locationLongitude)}
        </>
      )}
    </span>
  ) : (
    <span className="text-xs text-on-surface whitespace-nowrap">
      <span className="font-semibold">Latitude:</span>{" "}
      {formatCoordinate(parsedLocation?.locationLatitude)}
      <span className="mx-2 text-on-surface-variant">,</span>
      <span className="font-semibold">Longitude:</span>{" "}
      {formatCoordinate(parsedLocation?.locationLongitude)}
      {locationLogResult?.updated_at && (
        <>
          <span className="mx-2 text-on-surface-variant">,</span>
          <span className="font-semibold">Last updated:</span>{" "}
          {new Date(locationLogResult.updated_at).toLocaleString()}
        </>
      )}
      {appVersion != null && appVersion !== "" && (
        <>
          <span className="mx-2 text-on-surface-variant">,</span>
          <span className="font-semibold">App version:</span> {String(appVersion)}
        </>
      )}
    </span>
  );

  const getMapStatusMessage = () => {
    if (isBranchMapDataLoading) return "Loading branch map...";
    if (branchMapDataError) return branchMapDataError;
    if (!hasRetailerBranchIds) {
      return "Cannot load branch map: missing retailer or branch ID for this device.";
    }
    if (!storeCoordinates) {
      return "No branch map configuration found for this device's store.";
    }
    return "";
  };

  const mapStatusMessage = getMapStatusMessage();

  useEffect(() => {
    setFloor(DEFAULT_FLOOR_ALL);
  }, [retailerId, branchId]);

  useEffect(() => {
    if (locationSource === "mdm") return undefined;
    if (!canRenderMap || !mapContainerRef.current || !storeCenter) return undefined;

    setMapboxAccessToken();
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: MAPBOX_STYLE,
      center: [storeCenter.lng, storeCenter.lat],
      zoom: 18,
      bearing: MAPBOX_DEFAULT_BEARING,
    });
    mapRef.current = map;
    setMapReady(false);

    const handleLoad = () => {
      setMapReady(true);
      map.resize();
    };
    map.on("load", handleLoad);

    return () => {
      map.off("load", handleLoad);
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
  }, [canRenderMap, locationSource, retailerId, branchId, storeCoordinates, storeCenter]);

  useEffect(() => {
    if (locationSource === "mdm") return;
    if (!mapRef.current || !mapReady || !storeCoordinates) return;
    applyFloorImageLayer(mapRef.current, storeCoordinates, floor);
  }, [floor, locationSource, mapReady, storeCoordinates]);

  useEffect(() => {
    if (locationSource === "mdm") return;
    const map = mapRef.current;
    if (!map || !mapReady) return;

    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }

    if (deviceCoordinates) {
      const marker = new mapboxgl.Marker({ color: "#2563eb", scale: 0.9 })
        .setLngLat([deviceCoordinates.lng, deviceCoordinates.lat])
        .addTo(map);
      markerRef.current = marker;
      map.easeTo({
        center: [deviceCoordinates.lng, deviceCoordinates.lat],
        duration: 500,
      });
      return;
    }

    if (storeCenter) {
      map.easeTo({
        center: [storeCenter.lng, storeCenter.lat],
        duration: 500,
      });
    }
  }, [deviceCoordinates, locationSource, mapReady, storeCenter]);

  useEffect(() => {
    if (locationSource === "mdm") return undefined;
    if (!isLocationTabActive || !mapRef.current) return undefined;
    const frameId = requestAnimationFrame(() => {
      mapRef.current?.resize();
    });
    return () => cancelAnimationFrame(frameId);
  }, [isLocationTabActive, locationSource]);

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-4">
      <div className="shrink-0 bg-surface-container-high rounded-lg p-4">
        <div className="flex items-center gap-3 overflow-x-auto">
          <div className="inline-flex shrink-0 rounded-lg bg-surface-container-low p-0.5">
            <button
              type="button"
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                locationSource === "mdm"
                  ? "bg-primary text-white"
                  : "text-on-surface-variant hover:bg-surface-container-high"
              }`}
              onClick={() => onLocationSourceChange("mdm")}
            >
              MDM
            </button>
            <button
              type="button"
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                locationSource === "logs"
                  ? "bg-primary text-white"
                  : "text-on-surface-variant hover:bg-surface-container-high"
              }`}
              onClick={() => onLocationSourceChange("logs")}
            >
              Logs
            </button>
          </div>
          {locationDetails}
        </div>
      </div>

      {locationSource === "mdm" ? (
        <div className="flex flex-col flex-1 min-h-0 bg-surface-container-high rounded-lg overflow-hidden">
          <div className="flex-1 min-h-0 flex flex-col">
            <MdmLocationVersionMapPanel
              deviceEntries={historyMapEntries}
              focusedDeviceId={focusedHistoryId}
              locationSource="mdm"
              retailerId={retailerId}
              branchId={branchId}
              storeCoordinates={storeCoordinates}
              branchMapData={branchMapData}
              isBranchMapDataLoading={isBranchMapDataLoading}
              branchMapDataError={branchMapDataError}
              isMapActive={isLocationTabActive}
              fillHeight
              emptyCoordinatesMessage="No MDM location history coordinates for this device."
            />
          </div>

          <div className="shrink-0 border-t border-outline-variant/10 bg-surface-container-high">
            <button
              type="button"
              className="w-full px-4 py-2 flex items-center justify-between text-xs font-semibold text-on-surface hover:bg-surface-container-low/60 transition-colors"
              onClick={() => setIsHistoryListExpanded((prev) => !prev)}
            >
              <span className="flex items-center gap-2">
                <span className="material-symbols-outlined text-sm">history</span>
                Location History ({historyEntries.length})
              </span>
              <span className="material-symbols-outlined text-sm">
                {isHistoryListExpanded ? "expand_more" : "expand_less"}
              </span>
            </button>

            {isHistoryListExpanded && (
              <div className="max-h-[200px] overflow-auto border-t border-outline-variant/10">
                {historyEntries.length === 0 ? (
                  <p className="px-4 py-4 text-xs text-on-surface-variant text-center">
                    No location history stored for this device.
                  </p>
                ) : (
                  <table className="min-w-full text-left text-xs">
                    <thead className="bg-surface-container-low/50 sticky top-0 z-[1]">
                      <tr className="text-[10px] text-on-surface-variant uppercase tracking-wide">
                        <th className="px-3 py-2 font-bold whitespace-nowrap">#</th>
                        <th className="px-3 py-2 font-bold whitespace-nowrap">Agent Check-in</th>
                        <th className="px-3 py-2 font-bold whitespace-nowrap">Latitude</th>
                        <th className="px-3 py-2 font-bold whitespace-nowrap">Longitude</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-outline-variant/20">
                      {historyEntries.map((entry, index) => {
                        const isFocused = focusedHistoryId === entry.historyId;
                        return (
                          <tr
                            key={entry.historyId}
                            aria-selected={isFocused}
                            className={`cursor-pointer transition-colors border-l-4 ${
                              isFocused
                                ? "bg-primary/15 border-l-primary text-on-surface font-semibold shadow-[inset_0_0_0_1px_rgba(37,99,235,0.25)]"
                                : "border-l-transparent hover:bg-surface-container-low/60"
                            }`}
                            onClick={() =>
                              setFocusedHistoryId((prev) =>
                                prev === entry.historyId ? null : entry.historyId
                              )
                            }
                          >
                            <td
                              className={`px-3 py-2 whitespace-nowrap ${
                                isFocused ? "text-primary" : "text-on-surface-variant"
                              }`}
                            >
                              {isFocused ? (
                                <span className="inline-flex items-center gap-1">
                                  <span className="material-symbols-outlined text-sm leading-none">
                                    location_on
                                  </span>
                                  {index + 1}
                                </span>
                              ) : (
                                index + 1
                              )}
                            </td>
                            <td
                              className={`px-3 py-2 whitespace-nowrap ${
                                isFocused ? "text-on-surface" : "text-on-surface-variant"
                              }`}
                            >
                              {entry.agentCheckIn ? formatTime(entry.agentCheckIn) : "No Data"}
                            </td>
                            <td
                              className={`px-3 py-2 font-mono text-[11px] whitespace-nowrap ${
                                isFocused ? "text-on-surface" : "text-on-surface-variant"
                              }`}
                            >
                              {formatCoordinate(entry.latitude)}
                            </td>
                            <td
                              className={`px-3 py-2 font-mono text-[11px] whitespace-nowrap ${
                                isFocused ? "text-on-surface" : "text-on-surface-variant"
                              }`}
                            >
                              {formatCoordinate(entry.longitude)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
                <p className="px-4 py-2 text-[10px] text-on-surface-variant border-t border-outline-variant/10">
                  Click a row to focus its marker. Click again to return to the branch map view.
                  Newest records appear first (up to 50).
                </p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-col flex-1 min-h-0 bg-surface-container-high rounded-lg p-4 space-y-3">
          {showFloorPicker && canRenderMap && (
            <div className="flex items-center gap-2">
              <select
                id="mdm-location-floor"
                value={floor}
                onChange={(e) => setFloor(e.target.value)}
                className="text-xs rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-2 py-1.5 text-on-surface"
              >
                {floorOptions.map((floorOption) => (
                  <option key={floorOption} value={floorOption}>
                    {floorOption}
                  </option>
                ))}
                <option value={DEFAULT_FLOOR_ALL}>All floors</option>
              </select>
            </div>
          )}

          {mapStatusMessage && (
            <p className="text-xs text-on-surface-variant">{mapStatusMessage}</p>
          )}

          <div
            ref={mapContainerRef}
            className={`w-full flex-1 min-h-[50vh] rounded-lg overflow-hidden bg-surface-container-low ${
              canRenderMap ? "block" : "hidden"
            }`}
          />
        </div>
      )}
    </div>
  );
};

export default MdmDeviceLocationTab;
